import { useSyncExternalStore } from 'react'
import type { SetupStepId } from '@shared/setup'

/**
 * Tiny store shared by the setup host, the walkthrough and the few surfaces
 * that must step aside while setup is on screen.
 *
 * - `pending`: setup may still show this launch (not yet decided, or owed and
 *   not finished / dismissed). The walkthrough waits while this is true.
 * - `open`: the setup modal is on screen.
 * - `request`: an explicit open (Menu → Replay set up, a push tap, a deep link).
 * - `outstanding`: required steps still open on this device (Menu chip).
 */
export type SetupOpenRequest = {
  mode: 'manual' | 'deep-link'
  /** Start at this step (`overview` = the full first-run walk). */
  step?: SetupStepId | 'overview'
  /** Bumped per request so the same request twice still reopens. */
  nonce: number
}

export type SetupGateState = {
  pending: boolean
  open: boolean
  request: SetupOpenRequest | null
  outstanding: number | null
}

const INITIAL: SetupGateState = { pending: true, open: false, request: null, outstanding: null }

let state: SetupGateState = INITIAL
let nonce = 0
const listeners = new Set<() => void>()

function set(patch: Partial<SetupGateState>) {
  const next = { ...state, ...patch }
  if (
    next.pending === state.pending &&
    next.open === state.open &&
    next.request === state.request &&
    next.outstanding === state.outstanding
  ) {
    return
  }
  state = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getState(): SetupGateState {
  return state
}

export function getSetupGateState(): SetupGateState {
  return state
}

export function setSetupPending(pending: boolean): void {
  set({ pending })
}

export function setSetupOpen(open: boolean): void {
  set({ open })
}

export function setSetupOutstanding(outstanding: number | null): void {
  set({ outstanding })
}

/** Open setup on demand (Menu → Replay set up, push tap, `?setup=` link). */
export function requestSetup(request: Omit<SetupOpenRequest, 'nonce'>): void {
  nonce += 1
  set({ request: { ...request, nonce } })
}

export function clearSetupRequest(): void {
  set({ request: null })
}

/** Sign-out / account switch: setup must decide again for the next user. */
export function resetSetupGate(): void {
  state = INITIAL
  for (const listener of listeners) listener()
}

/** Setup is not going to show this launch (or has been finished / dismissed). */
export function isSetupSettled(): boolean {
  return !state.pending && !state.open
}

export function useSetupGate(): SetupGateState {
  return useSyncExternalStore(subscribe, getState, getState)
}

/** For the walkthrough's auto-start gate: true while setup may still show this launch. */
export function useSetupPending(): boolean {
  return useSyncExternalStore(subscribe, () => state.pending || state.open, () => state.pending || state.open)
}

/** Contextual permission banners hide while the setup modal is on screen. */
export function useIsSetupOpen(): boolean {
  return useSyncExternalStore(subscribe, () => state.open, () => state.open)
}
